import { useState, type ReactNode, type ReactElement } from 'react';
import cn from 'classnames';
import IconArrowDown from '@creditflowresearch/uilib/Icon/IconArrowDown';
import IconArrowUp from '@creditflowresearch/uilib/Icon/IconArrowUp';
import Flex, { type IFlexProps } from '@creditflowresearch/uilib/Flex';

import styles from './tile.module.scss';

interface ITileHeaderProps extends Omit<IFlexProps, 'children' | 'className' | 'dataTest'> {
  title: ReactNode,
  children?: ReactNode,
  rightSlot?: ReactNode,
  className?: string,
  dataTest?: string,
  noDesktopBottomBorder?: boolean,
  hideTitleOnDesktop?: boolean,
  isExpanded?: boolean,
  onToggle?: () => void,
}

const TileHeader = ({
  title,
  children,
  rightSlot,
  className,
  dataTest,
  noDesktopBottomBorder = false,
  hideTitleOnDesktop = false,
  isExpanded: isExpandedProp,
  onToggle,
  ...flexProps
}: ITileHeaderProps): ReactElement => {
  const [internalExpanded, setInternalExpanded] = useState<boolean>(false);
  const isControlled = isExpandedProp !== undefined && onToggle !== undefined;
  const expanded = isControlled ? isExpandedProp : internalExpanded;

  const toggleExpanded = (): void => {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  const ChevronIcon = expanded ? IconArrowUp : IconArrowDown;

  return (
    <Flex
      direction={ { sm: 'column', md: 'column', xl: 'row' } }
      align={ { sm: 'stretch', md: 'stretch', xl: 'center' } }
      justify="space-between"
      gap={ { sm: 20, md: 20 } }
      { ...flexProps }
      className={ cn(
        styles.header,
        noDesktopBottomBorder ? styles.headerBorderMobileOnly : styles.headerBorder,
        className,
      ) }
      dataTest={ dataTest }
    >
      <Flex
        align="center"
        gap={ 10 }
        justify={ { sm: 'space-between', md: 'space-between' } }
        className={ cn(styles.titleRow, { [styles.titleRowSpaceBetween]: !!rightSlot }) }
      >
        { hideTitleOnDesktop
          ? <div className={ styles.hiddenOnDesktop }>{ title }</div>
          : title }
        { children && (
        <button
          type="button"
          className={ styles.chevron }
          aria-label="Toggle toolbar"
          aria-expanded={ expanded }
          onClick={ toggleExpanded }
          data-test={ dataTest ? `${ dataTest }Chevron` : undefined }
        >
          <ChevronIcon />
        </button>
          ) }
        { rightSlot }
      </Flex>
      { children && (
        <div className={ cn(styles.headerContent, { [styles.headerContentExpanded]: expanded }) }>
          { children }
        </div>
      ) }
    </Flex>
  );
};

export { TileHeader };
export type { ITileHeaderProps };
